import { MenuItemWithIcon, useRunWithFlag } from "@msys/ui";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ItemSelect } from "../ItemSelect.js";
import type { ItemSelect_ItemFragment } from "../ItemSelect.generated.js";

interface Props {
  getValidMoveTargets: () => Promise<ItemSelect_ItemFragment[]>;
  onMove: (targetItemId: string) => void | Promise<void>;
  onMoved?: () => void;
  disabled?: boolean;
}

export const MoveToSubMenuItem = ({
  getValidMoveTargets,
  onMove,
  onMoved,
  disabled,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  return (
    <>
      <MenuItemWithIcon
        icon={<TrendingFlatIcon />}
        disabled={disabled}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
      >
        {t("Move to…", { ns: "QuoteItem" })}
      </MenuItemWithIcon>
      {anchorEl && (
        <MoveToSubMenuContent
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          getValidMoveTargets={getValidMoveTargets}
          onMove={onMove}
          onMoved={onMoved}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Submenu content — rendered only when open, fetches targets lazily
// ---------------------------------------------------------------------------

interface SubMenuContentProps {
  anchorEl: HTMLElement;
  onClose: () => void;
  getValidMoveTargets: () => Promise<ItemSelect_ItemFragment[]>;
  onMove: (targetItemId: string) => void | Promise<void>;
  onMoved?: () => void;
}

const MoveToSubMenuContent = ({
  anchorEl,
  onClose,
  getValidMoveTargets,
  onMove,
  onMoved,
}: SubMenuContentProps) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(
    null
  );
  const [isMoving, runMove] = useRunWithFlag();
  const [validTargets, setValidTargets] = React.useState<
    ItemSelect_ItemFragment[] | null
  >(null);

  React.useEffect(() => {
    let cancelled = false;
    getValidMoveTargets().then(targets => {
      if (!cancelled) setValidTargets(targets);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isLoading = validTargets === null;

  return (
    <Menu
      anchorEl={anchorEl}
      open
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      onClose={onClose}
      onClick={e => e.stopPropagation()}
    >
      <MenuItem
        sx={theme => ({ minWidth: theme.layout.filters.popoverWidth.xl })}
      >
        {isLoading ? (
          <Stack direction="row" justifyContent="center" flex={1} py={0.5}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            flex={1}
            onKeyDown={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
          >
            <ItemSelect
              inputLabel=""
              placeholder={t("Target item", { ns: "QuoteItem" })}
              items={validTargets}
              itemId={selectedItemId}
              onChange={item => setSelectedItemId(item?.id ?? null)}
              size="extra-small"
            />
            <Button
              variant="contained"
              size="extra-small"
              disabled={!selectedItemId || isMoving}
              onClick={() => {
                if (selectedItemId) {
                  runMove(async () => {
                    await onMove(selectedItemId);
                    onClose();
                    onMoved?.();
                  });
                }
              }}
            >
              {t("Move", { ns: "Global" })}
            </Button>
          </Stack>
        )}
      </MenuItem>
    </Menu>
  );
};
